import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import find from 'lodash/find';
import get from 'lodash/get';
import { countrycodes } from 'nallian-shared-ui/lib/lib/countrycodes';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { hasSomeFeatures } from 'nallian-shared-ui/lib/lib/routesHelper';
import { GetFeatures } from 'nallian-shared-ui/lib/selectors';
import {
  Loader,
  Grid,
  Table,
  Header,
  Flag,
  Flex,
  Button,
  Link,
} from 'nallian-shared-ui/lib/components';
import { formatDateTime, formatRelativeTime } from 'nallian-shared-ui/lib/lib/dateTime';
import ActiveState from '../ActiveState';
import { CONTACT_ACTIONS } from '../../constants';
import NewContact from '../contacts/NewContact';
import EditContact from '../contacts/EditContact';
import RemoveContact from '../contacts/RemoveContact';
import { TableIcon } from '../../../app/styles';
import ManageOrganisationLogo from '../logo/ManageOrganisationLogo';
import CommunityLookup from './CommunityLookup';

const CONTACT_CREATE_RIGHTS = ['contacts/create', 'contacts/create/other-organisations'];
const CONTACT_UPDATE_RIGHTS = ['contacts/update', 'contacts/update/other-organisations'];
const CONTACT_DELETE_RIGHTS = ['contacts/delete', 'contacts/delete/other-organisations'];

const Details = ({
  organisationId,
  urlRoot,
  organisationFetch,
  organisationContactsFetch,
  organisationAdminsFetch,
}) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();

  const features = useSelector(GetFeatures);
  const canSeeCommunity = useMemo(
    () => hasSomeFeatures(features, ['PLATFORMMGMT', 'COMMUNITYMGMT']),
    [features]
  );

  const organisation = organisationFetch.value;

  return (
    <>
      <HasSomeRights rights={CONTACT_CREATE_RIGHTS}>
        <NewContact
          organisationId={organisationId}
          open={organisationId && action === CONTACT_ACTIONS.CREATE_CONTACT}
          returnRoute={urlRoot}
        />
      </HasSomeRights>
      <HasSomeRights rights={CONTACT_UPDATE_RIGHTS}>
        <EditContact
          organisationId={organisationId}
          contactId={actionId}
          open={organisationId && actionId && action === CONTACT_ACTIONS.EDIT_CONTACT}
          returnRoute={urlRoot}
        />
      </HasSomeRights>
      <HasSomeRights rights={CONTACT_DELETE_RIGHTS}>
        <RemoveContact
          organisationId={organisationId}
          contactId={actionId}
          open={organisationId && actionId && action === CONTACT_ACTIONS.REMOVE_CONTACT}
          returnRoute={urlRoot}
        />
      </HasSomeRights>
      <Loader
        active={
          organisationFetch.pending ||
          organisationAdminsFetch.pending ||
          organisationContactsFetch.pending
        }
        inline="centered"
      />
      {organisationFetch.fulfilled && (
        <>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header>{t('general')}</Header>
              <Table>
                <Table.Row>
                  <Table.HeaderCell width={4}>{t('name')}</Table.HeaderCell>
                  <Table.Cell>{organisation.organisationName}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell>{t('description')}</Table.HeaderCell>
                  <Table.Cell>{organisation.description}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell>{t('parentOrganisation')}</Table.HeaderCell>
                  <Table.Cell>{organisation.parentOrganisationName || '-'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell>{t('createdByOrganisation')}</Table.HeaderCell>
                  <Table.Cell>{organisation.createdByOrganisationName || '-'}</Table.Cell>
                </Table.Row>

                {canSeeCommunity && (
                  <Table.Row>
                    <Table.HeaderCell>{t('defaultCommunity')}</Table.HeaderCell>
                    <Table.Cell>
                      <CommunityLookup id={organisation.defaultCommunityId} />
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table>
              <Header>{t('location')}</Header>
              <Table>
                <Table.Row>
                  <Table.HeaderCell width={4}>{t('city')}</Table.HeaderCell>
                  <Table.Cell>{organisation.city ? organisation.city : 'No data'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell>{t('country')}</Table.HeaderCell>
                  <Table.Cell>
                    {organisation.countryCode ? (
                      <>
                        <Flag name={organisation.countryCode.toLowerCase()} />{' '}
                        {get(
                          find(countrycodes, {
                            code: organisation.countryCode,
                          }),
                          'name',
                          organisation.countryCode
                        )}
                      </>
                    ) : (
                      'No data'
                    )}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell width={4}>{t('timezone')}</Table.HeaderCell>
                  <Table.Cell>{organisation.timeZone ? organisation.timeZone : '-'}</Table.Cell>
                </Table.Row>
              </Table>
              {organisationAdminsFetch.value?.length > 0 && (
                <>
                  <Header>{t('administrators')}</Header>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width="5">{t('name')}</Table.HeaderCell>
                        <Table.HeaderCell width="5">{t('email')}</Table.HeaderCell>
                        <Table.HeaderCell width="5">{t('phoneNumber')}</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {(organisationAdminsFetch.value || []).map(admin => (
                        <Table.Row key={admin.id}>
                          <Table.Cell>{admin.fullName}</Table.Cell>
                          <Table.Cell>
                            <a href={`mailto:${admin.primaryEmailAddress}`}>
                              {admin.primaryEmailAddress}
                            </a>
                          </Table.Cell>
                          <Table.Cell>{admin.primaryPhoneNumber}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </>
              )}
            </Grid.Column>
            <Grid.Column>
              <Header>{t('status')}</Header>
              <Table>
                <Table.Row>
                  <Table.HeaderCell width={4}>{t('activeStatus')}</Table.HeaderCell>
                  <Table.Cell>
                    <ActiveState value={organisation.activeStateId} />
                  </Table.Cell>
                </Table.Row>
              </Table>
              <Header>{t('metaData')}</Header>
              <Table>
                <Table.Row>
                  <Table.HeaderCell width={4}>{t('createdOn')}</Table.HeaderCell>
                  <Table.Cell>
                    {`${formatDateTime(organisation.createdOn)}`}
                    &nbsp;-&nbsp;
                    {formatRelativeTime(organisation.createdOn)}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.HeaderCell>{t('lastModifiedOn')}</Table.HeaderCell>
                  <Table.Cell>
                    {`${formatDateTime(organisation.modifiedOn)}`}
                    &nbsp;-&nbsp;
                    {formatRelativeTime(organisation.modifiedOn)}
                  </Table.Cell>
                </Table.Row>
              </Table>
              <ManageOrganisationLogo organisation={organisation} />
            </Grid.Column>
          </Grid>
          <Grid columns={1} stackable>
            <Grid.Column>
              <Flex fluid justifyContent="space-between">
                <Header>{t('contacts')}</Header>
                <HasSomeRights rights={CONTACT_CREATE_RIGHTS}>
                  <Link to={`${urlRoot}/${CONTACT_ACTIONS.CREATE_CONTACT}`}>
                    <Button size="mini" icon="plus" content={t('addContact')} linkbutton />
                  </Link>
                </HasSomeRights>
              </Flex>
              {organisationContactsFetch.value?.length > 0 ? (
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <HasSomeRights rights={CONTACT_UPDATE_RIGHTS}>
                        <Table.HeaderCell />
                      </HasSomeRights>
                      <Table.HeaderCell width="5">{t('description')}</Table.HeaderCell>
                      <Table.HeaderCell width="6">{t('email')}</Table.HeaderCell>
                      <Table.HeaderCell width="5">{t('phoneNumber')}</Table.HeaderCell>
                      <HasSomeRights rights={CONTACT_DELETE_RIGHTS}>
                        <Table.HeaderCell />
                      </HasSomeRights>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(organisationContactsFetch.value || []).map(contact => (
                      <Table.Row key={contact.id}>
                        <HasSomeRights rights={CONTACT_UPDATE_RIGHTS}>
                          <Table.Cell>
                            <Link to={`${urlRoot}/${CONTACT_ACTIONS.EDIT_CONTACT}/${contact.id}`}>
                              <TableIcon color="gray" name="eye" />
                            </Link>
                          </Table.Cell>
                        </HasSomeRights>
                        <Table.Cell width="5">{contact.description}</Table.Cell>
                        <Table.Cell width="6">
                          <a href={`mailto:${contact.emailAddress}`}>{contact.emailAddress}</a>
                        </Table.Cell>
                        <Table.Cell width="5">{contact.phoneNumber}</Table.Cell>
                        <HasSomeRights rights={CONTACT_DELETE_RIGHTS}>
                          <Table.Cell>
                            <Link to={`${urlRoot}/${CONTACT_ACTIONS.REMOVE_CONTACT}/${contact.id}`}>
                              <TableIcon name="trash" />
                            </Link>
                          </Table.Cell>
                        </HasSomeRights>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : (
                <div>
                  <i>{t('noContactsYet')}</i>
                </div>
              )}
            </Grid.Column>
          </Grid>
        </>
      )}
    </>
  );
};

Details.propTypes = {
  organisationFetch: PropTypes.object.isRequired,
  organisationContactsFetch: PropTypes.object.isRequired,
  organisationAdminsFetch: PropTypes.object.isRequired,
  urlRoot: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
};

export default Details;
